// First import: telemetry wraps the app before anything else runs.
import "./instrument";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RootError, RootNotFound } from "./components/boundaries";
import { ThemeProvider } from "./components/theme-provider";
import { ToastProvider } from "./components/toaster";
import { ApiError } from "./lib/api";
import { keys } from "./lib/keys";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

/** A 401 anywhere means the session is gone: drop it and the root route sends us to /login. */
function onAuthError(error: unknown) {
  if (error instanceof ApiError && error.status === 401) {
    queryClient.setQueryData(keys.session, null);
    router.navigate({ to: "/login", search: { redirect: location.pathname } });
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: { queries: { retry: 1, staleTime: 15_000, refetchOnWindowFocus: false } },
});

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultErrorComponent: RootError,
  defaultNotFoundComponent: RootNotFound,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
